/**
 * Search utility functions to filter data across different entities
 */

import { formatDuration, formatTimeStamp, getCallDurationInSeconds } from '../api/callData';

/**
 * Searches through call data based on a query
 * 
 * Searchable columns (all except Feedback):
 * - Equipment: equipment type and model
 * - Phone Number: caller ID
 * - Duration: formatted duration (e.g., "5 min", "2 hours")
 * - Start Time: formatted timestamp (e.g., "2024-01-15", "2:30 PM")
 * - Sentiment: trend values (improving, declining, stable, neutral) and synonyms
 * - Success Evaluation: score status, success score numbers, and ranges (high/medium/low)
 * - Booking Status: order status (confirmed, pending, cancelled, completed, "no booking")
 * 
 * @param {Array} calls - Array of call objects
 * @param {String} query - Search query
 * @returns {Array} - Filtered calls that match the query
 */
export const searchCalls = (calls, query) => {
  if (!query || !calls || !calls.length) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  
  return calls.filter(call => {
    // Search in session ID (for chat history, especially Kronk)
    if (call.metadata?.session_id && 
        call.metadata.session_id.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in stream_sid (Call Id for celeste/helios)
    if (call.metadata?.stream_sid && 
        call.metadata.stream_sid.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in caller ID (Phone Number column)
    if (call.metadata?.caller_id && 
        call.metadata.caller_id.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in transcript content
    if (call.transcript && 
        call.transcript.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in sentiment analysis (Sentiment column)
    if (call.analytics?.sentiment_analysis?.trend && 
        call.analytics.sentiment_analysis.trend.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in sentiment trend descriptions (e.g., "improving", "declining", "stable", "neutral")
    if (call.analytics?.sentiment_analysis?.trend) {
      const trend = call.analytics.sentiment_analysis.trend.toLowerCase();
      if (trend.includes(normalizedQuery) || 
          (trend === 'improving' && (normalizedQuery.includes('up') || normalizedQuery.includes('better'))) ||
          (trend === 'declining' && (normalizedQuery.includes('down') || normalizedQuery.includes('worse'))) ||
          (trend === 'stable' && (normalizedQuery.includes('stable') || normalizedQuery.includes('steady'))) ||
          (trend === 'neutral' && (normalizedQuery.includes('neutral') || normalizedQuery.includes('normal')))) {
        return true;
      }
    }
    
    // Search in duration (Duration column)
    const durationSec = getCallDurationInSeconds(call);
    if (durationSec > 0) {
      const durationText = formatDuration(durationSec).toLowerCase();
      if (durationText.includes(normalizedQuery)) {
        return true;
      }
    }
    
    // Search in start time (Start Time column)
    if (call.metadata?.timestamp) {
      const startTimeText = formatTimeStamp(call.metadata.timestamp).toLowerCase();
      if (startTimeText.includes(normalizedQuery)) {
        return true;
      }
    }
    
    // Search in success evaluation (Success Evaluation column)
    if (call.score_status && 
        call.score_status.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in success score numbers
    if (call.success_score && 
        call.success_score.toString().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in success score ranges (e.g., "high", "medium", "low")
    if (call.success_score) {
      let scoreRange = '';
      if (call.success_score >= 7) scoreRange = 'high';
      else if (call.success_score >= 4) scoreRange = 'medium';
      else scoreRange = 'low';
      
      if (scoreRange.includes(normalizedQuery)) {
        return true;
      }
    }
    
    // Search in booking status (Booking Status column)
    if (call.booking_info || call.cart_data) {
      const bookingData = call.cart_data || call.booking_info;
      const orderStatus = getOrderStatus(bookingData, call.booking_resolved);
      if (orderStatus.toLowerCase().includes(normalizedQuery)) {
        return true;
      }
      
      // Search in equipment requested (Equipment column) - handle both structures
      if (call.cart_data?.full_cart?.items) {
        // New cart_data structure
        if (call.cart_data.full_cart.items.some(item => 
          (item.name && item.name.toLowerCase().includes(normalizedQuery)) || 
          (item.model && item.model.toLowerCase().includes(normalizedQuery))
        )) {
          return true;
        }
      } else if (call.booking_info?.equipment_requested) {
        // Legacy booking_info structure
        if (call.booking_info.equipment_requested.some(equipment => 
          (equipment.type && equipment.type.toLowerCase().includes(normalizedQuery)) || 
          (equipment.model && equipment.model.toLowerCase().includes(normalizedQuery))
        )) {
          return true;
        }
      }
      
      // Search in customer info - handle both structures
      const customerInfo = call.cart_data?.full_cart?.customer_info || call.booking_info?.customer_info;
      if (customerInfo) {
        return Object.values(customerInfo).some(value => 
          value && value.toString().toLowerCase().includes(normalizedQuery)
        );
      }
    } else {
      // Search for "no booking" when there's no booking info
      if (normalizedQuery.includes('no') && normalizedQuery.includes('booking')) {
        return true;
      }
    }
    
    return false;
  });
};

/**
 * Searches through order data based on a query
 * @param {Array} calls - Array of call objects with booking info
 * @param {String} query - Search query
 * @returns {Array} - Filtered orders that match the query
 */
export const searchOrders = (calls, query) => {
  if (!query || !calls || !calls.length) return [];
  
  // First filter to only calls with booking info or cart data (orders)
  const orders = calls.filter(call => {
    if (call.cart_data) {
      // New cart_data structure
      return call.cart_data.full_cart?.items?.length > 0 ||
             call.cart_data.full_cart?.customer_info ||
             call.cart_data.full_cart?.delivery;
    } else if (call.booking_info) {
      // Legacy booking_info structure
      return call.booking_info.equipment_requested?.length > 0 ||
             call.booking_info.customer_info ||
             call.booking_info.delivery_info;
    }
    return false;
  });
  
  // Then search within those orders
  return searchCalls(orders, query);
};

/**
 * Searches through Shopify orders based on a query
 * @param {Array} shopifyOrders - Array of Shopify order objects
 * @param {String} query - Search query
 * @returns {Array} - Filtered orders that match the query
 */
export const searchShopifyOrders = (shopifyOrders, query) => {
  if (!query || !shopifyOrders || !shopifyOrders.length) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  
  return shopifyOrders.filter(order => {
    // Search in order number
    if (order.order_number && 
        order.order_number.toString().toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in order name (Shopify order name like #1001)
    if (order.name && 
        order.name.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in customer name
    if (order.customer) {
      const firstName = order.customer.first_name?.toLowerCase() || '';
      const lastName = order.customer.last_name?.toLowerCase() || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName.includes(normalizedQuery)) {
        return true;
      }
    }
    
    // Search in email
    if (order.email && 
        order.email.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in line items
    if (order.line_items && Array.isArray(order.line_items)) {
      const itemsMatch = order.line_items.some(item => {
        if (item.title && item.title.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        if (item.variant_title && item.variant_title.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        if (item.sku && item.sku.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        return false;
      });
      if (itemsMatch) return true;
    }
    
    // Search in order status
    if (order.status && 
        order.status.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in fulfillment status
    if (order.fulfillment_status && 
        order.fulfillment_status.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in financial status
    if (order.financial_status && 
        order.financial_status.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in total price
    if (order.total_price && 
        order.total_price.toString().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in created date
    if (order.created_at) {
      try {
        const date = new Date(order.created_at);
        const dateStr = date.toLocaleDateString().toLowerCase();
        if (dateStr.includes(normalizedQuery)) {
          return true;
        }
      } catch (e) {
        // Ignore date parsing errors
      }
    }
    
    return false;
  });
};

/**
 * Get order status from booking info
 * @param {Object} bookingInfo - Booking information object
 * @param {Boolean} bookingResolved - Whether the booking is resolved
 * @returns {String} - Status string
 */
export const getOrderStatus = (bookingInfo, bookingResolved) => {
  if (bookingResolved) return 'Completed';
  
  // Handle new cart_data structure - check has_customer_info
  if (bookingInfo?.summary?.has_customer_info !== undefined) {
    return bookingInfo.summary.has_customer_info ? 'Confirmed' : 'Incomplete';
  }
  
  // Handle legacy booking_info structure
  if (bookingInfo?.booking_status?.status) {
    const status = bookingInfo.booking_status.status;
    let readableStatus = status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    // Normalize 'Confirmed' to 'Booking Confirmed' to handle backend data inconsistency
    if (readableStatus === 'Confirmed') {
      return 'Booking Confirmed';
    }
    return readableStatus;
  }
  return 'Pending';
};

/**
 * Get equipment list from booking info
 * @param {Object} bookingInfo - Booking information object
 * @returns {String} - Formatted equipment list
 */
export const getEquipmentList = (bookingInfo) => {
  // Handle new cart_data structure
  if (bookingInfo?.full_cart?.items?.length > 0) {
    return bookingInfo.full_cart.items
      .map(item => `${item.name || 'Unknown Item'}${item.model ? ` (${item.model})` : ''}`)
      .join(', ');
  }
  // Handle legacy booking_info structure
  if (bookingInfo?.equipment_requested?.length > 0) {
    return bookingInfo.equipment_requested
      .map(equipment => `${equipment.type || 'Unknown Equipment'}${equipment.model ? ` (${equipment.model})` : ''}`)
      .join(', ');
  }
  return 'Equipment details not specified';
};

/**
 * Searches through ticket data based on a query
 * @param {Array} tickets - Array of ticket objects
 * @param {String} query - Search query
 * @returns {Array} - Filtered tickets that match the query
 */
export const searchTickets = (tickets, query) => {
  if (!query || !tickets || !tickets.length) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  
  return tickets.filter(ticket => {
    // Search in ticket number
    if (ticket.ticketNumber && 
        ticket.ticketNumber.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in subject
    if (ticket.subject && 
        ticket.subject.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in description
    if (ticket.description && 
        ticket.description.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in email/assigned to
    if (ticket.email && 
        ticket.email.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in priority
    if (ticket.priority && 
        ticket.priority.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in status
    if (ticket.status && 
        ticket.status.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in classification
    if (ticket.classification && 
        ticket.classification.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    return false;
  });
};

/**
 * Searches through inventory categories based on a query
 * @param {Object} equipmentData - Equipment data object with categories
 * @param {String} query - Search query
 * @returns {Array} - Filtered categories that match the query
 */
export const searchInventory = (equipmentData, query) => {
  if (!query || !equipmentData || !equipmentData.categories || !equipmentData.categories.length) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  
  return equipmentData.categories.filter(category => {
    // Search in category name
    if (category.parentCategory && 
        category.parentCategory.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in typical uses
    if (category.typicalUses && 
        category.typicalUses.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in typical flow
    if (category.typicalFlow && 
        category.typicalFlow.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in defaults
    if (category.defaults && 
        category.defaults.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in qualifying questions
    if (category.qualifyingQuestions && 
        category.qualifyingQuestions.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in special notes
    if (category.specialNotes && 
        category.specialNotes.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in pricing considerations
    if (category.pricingConsiderations && 
        category.pricingConsiderations.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in child items
    if (category.childItems && category.childItems.length > 0) {
      return category.childItems.some(child => {
        // Search in child category name
        if (child.childCategory && 
            child.childCategory.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        
        // Search in model
        if (child.model && 
            child.model.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        
        // Search in brand
        if (child.brand && 
            child.brand.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        
        // Search in customer reference
        if (child.customerReference && 
            child.customerReference.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        
        // Search in common synonyms
        if (child.commonSynonyms && 
            child.commonSynonyms.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        
        // Search in key selling points
        if (child.keySellingPoints && 
            child.keySellingPoints.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        
        // Search in description
        if (child.description && 
            child.description.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        
        // Search in pricing
        if (child.pricing && 
            child.pricing.toLowerCase().includes(normalizedQuery)) {
          return true;
        }
        
        return false;
      });
    }
    
    return false;
  });
};